import saveAs from 'file-saver';
import { uploadFromBase64, getBlobMetadata } from './FilesHelper';
import { appContext } from '../AppContext';
import { apimCall, publicShareApimCall } from '../helpers/api';
import { WrappedResponse } from 'gf-types';
import { AxiosResponse } from 'axios';
import { GoFormzServices } from '../AppSettings';

export const FileManagementFileType = {
  Embedded: 0,
  Original: 1,
  Attachment: 2
};

export type SasResponse = {
  account: string;
  accountUrl: string;
  container: string;
  containerUrl: string;
  expiration: string;
  fileStoragePrefix: string;
  guids: string[];
  signature: string;
};

export function getSas(idCount?: number): Promise<SasResponse> {
  return apimCall<WrappedResponse<SasResponse>>(GoFormzServices.FileService, `/files/sas?ids=${idCount || 1}`).then((json) => {
    return json.data.data;
  });
}

// TODO: type me please!
const getMetadataForFile = (file, fileId, fileName, description, formId, width, height, originalFileId, type) => {
  return {
    id: fileId,
    name: fileName,
    description,
    extension: file.name.split('.').pop(),
    formId,
    metadata: {
      contentType: file.type,
      size: file.size,
      width,
      height
    },
    originalFileId,
    type
  };
};

export const getMetadataForJpg = (
  dataUri: string,
  fileId: string,
  fileName: string,
  description: string,
  formId: string,
  width: number,
  height: number,
  originalFileId: string,
  type: number
) => {
  return {
    id: fileId,
    name: fileName,
    description,
    extension: 'jpg',
    formId,
    metadata: {
      contentType: 'image/jpeg',
      size: dataUri.length,
      width,
      height
    },
    originalFileId,
    type
  };
};

type PrepareBlobApiResponse = {
  account: string;
  accountUrl: string;
  blobId: string;
  container: string;
  containerUrl: string;
  expiration: string;
  fileStoragePrefix: string;
  guids: string[];
  signature: string;
};

export type PrepareBlobSasResponse = {
  fileId: string;
  attachmentId: string;
  account: string;
  accountUrl: string;
  signature: string;
  container: string;
  containerUrl: string;
  fileStoragePrefix: string;
};

async function prepareBlob(meta, createLegacyFileDefinition, createAttachment, fieldId): Promise<PrepareBlobSasResponse> {
  const state = appContext.store.getState();
  const sharedAccessToken = state.formeditor.sharedAccessToken;
  let blobId = '';
  let fileManagementResult: AxiosResponse<WrappedResponse<PrepareBlobApiResponse>> = null;
  if (sharedAccessToken) {
    const content = meta;
    content.sessionId = state.formeditor.sessionId;
    fileManagementResult = await publicShareApimCall<WrappedResponse<PrepareBlobApiResponse>>(`/files/prepareblob/${fieldId}`, 'POST', content);
  } else {
    fileManagementResult = await apimCall<WrappedResponse<PrepareBlobApiResponse>>(GoFormzServices.FileService, `/files/prepareblob`, 'POST', meta);
  }
  blobId = fileManagementResult.data.data.blobId;

  let attachmentId: string;
  if (createLegacyFileDefinition) {
    const files = [
      {
        id: blobId,
        url: `${fileManagementResult.data.data.containerUrl}/${fileManagementResult.data.data.fileStoragePrefix}/${fileManagementResult.data.data.blobId}`,
        width: meta.metadata.width,
        height: meta.metadata.height
      }
    ];
    let legacyFileResult = null;
    if (sharedAccessToken) {
      legacyFileResult = await publicShareApimCall(`/files/metadata?createAttachment=${createAttachment}`, 'POST', files);
    } else {
      legacyFileResult = await apimCall(GoFormzServices.FileService, `/files/metadata?createAttachment=${createAttachment}`, 'POST', files);
    }
    const createFileResult = legacyFileResult.data;
    attachmentId = createFileResult[0].attachmentId;
  }

  const sas: PrepareBlobSasResponse = {
    fileId: blobId,
    attachmentId,
    account: fileManagementResult.data.data.account,
    accountUrl: fileManagementResult.data.data.account,
    signature: fileManagementResult.data.data.signature,
    container: fileManagementResult.data.data.container,
    containerUrl: fileManagementResult.data.data.containerUrl,
    fileStoragePrefix: fileManagementResult.data.data.fileStoragePrefix
  };
  return sas;
}

export const uploadFileFromBase64 = async (
  dataUri: string,
  width: number,
  height: number,
  createAttachment: boolean,
  createLegacyFileDefinition: boolean,
  formId?: string,
  fileManagementType?: number,
  fileName?: string,
  description?: string,
  originalFileId?: string,
  fieldId?: string
) => {
  const meta = getMetadataForJpg(dataUri, null, fileName, description, formId, width, height, originalFileId, fileManagementType);
  const sas = await prepareBlob(meta, createLegacyFileDefinition, createAttachment, fieldId);
  const blobId = sas.fileId;
  const result = await uploadFromBase64(dataUri, blobId, sas);

  return {
    fileId: sas.fileId,
    attachmentId: sas.attachmentId,
    url: result.url
  };
};

export async function uploadFileFromBrowser(
  file: File,
  createAttachment: boolean,
  fileName: string,
  description: string,
  formId: string,
  width: number,
  height: number,
  originalFileId: string,
  type: number,
  createLegacyFileDefinition: boolean,
  fieldId: string,
  isFileAttachment?: boolean
) {
  //no blobid yet
  const meta = getMetadataForFile(file, null, fileName, description, formId, width, height, originalFileId, type);
  const sas = await prepareBlob(meta, createLegacyFileDefinition, createAttachment, fieldId);
  const signature = sas.signature.substring(1);

  const { BlobServiceClient } = await import('@azure/storage-blob');
  const blobServiceClient = BlobServiceClient.fromConnectionString(`BlobEndpoint=${sas.account};SharedAccessSignature=${signature}`);
  const containerClient = blobServiceClient.getContainerClient(sas.container);
  const blockBlobClient = containerClient.getBlockBlobClient(`${sas.fileStoragePrefix}/${sas.fileId}`);

  await blockBlobClient.uploadData(file, {
    blobHTTPHeaders: {
      blobContentType: file.type,
      blobContentDisposition: `attachment; filename=${encodeURIComponent(file.name)}`
    },
    metadata: getBlobMetadata(isFileAttachment)
  });

  return {
    id: sas.fileId,
    url: `${sas.containerUrl}/${sas.fileStoragePrefix}/${sas.fileId}`,
    fileId: sas.fileId,
    attachmentId: sas.attachmentId
  };
}

export async function uploadFromBrowser(file: File | Blob, id?: string) {
  const sas = await getSas();
  const blobId = id || sas.guids.pop();
  const signature = sas.signature.substring(1);

  const { BlobServiceClient } = await import('@azure/storage-blob');
  const blobServiceClient = BlobServiceClient.fromConnectionString(`BlobEndpoint=${sas.accountUrl};SharedAccessSignature=${signature}`);
  const containerClient = blobServiceClient.getContainerClient(sas.container);
  const blockBlobClient = containerClient.getBlockBlobClient(`${sas.fileStoragePrefix}/${blobId}`);

  await blockBlobClient.uploadData(file, {
    blobHTTPHeaders: {
      blobContentType: file.type
    },
    metadata: getBlobMetadata()
  });

  return {
    id: blobId,
    url: `${sas.containerUrl}/${sas.fileStoragePrefix}/${blobId}`,
    blobName: `${sas.fileStoragePrefix}/${blobId}`
  };
}

export const uploadFileFromUrl = async (url: string, width: number, height: number, formId: string, fieldId?: string) => {
  const response = await fetch(url);
  if (response.status >= 400) {
    throw new Error(`${response.status} : ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type');
  const data = await response.arrayBuffer();
  const imageStr = arrayBufferToBase64(data);
  const dataUri = `data:${contentType};base64,${imageStr}`;

  return uploadFileFromBase64(dataUri, width, height, true, true, formId, FileManagementFileType.Embedded, null, null, null, fieldId);
};

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = [].slice.call(new Uint8Array(buffer));

  // eslint-disable-next-line no-return-assign
  bytes.forEach((b) => (binary += String.fromCharCode(b)));

  return window.btoa(binary);
}

export const loadFileFromUrl = async (url): Promise<Blob> =>
  await new Promise((callback) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob'; //force the HTTP response, response-type header to be blob
    xhr.onload = function () {
      callback(xhr.response);
    };
    xhr.send();
  });

export const loadFileAsDataUrl = (file: File | Blob) =>
  new Promise<TypeMePlease>((callback) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      callback(fileReader.result);
    };
    fileReader.readAsDataURL(file);
  });

export function saveFileAs(file: Blob, name: string) {
  saveAs(file, name);
}

export const getFileExtension = (file: File) => {
  return file?.name?.match(/\.([^.]+)$/)[1];
};

export const getFileNameSansExtension = (file: File) => {
  const fileExtension = getFileExtension(file);
  if (fileExtension) {
    return file?.name?.replace(fileExtension, '').slice(0, -1);
  }
  return file?.name;
};

export const loadDataUrlAsFile = (dataURI: string) => {
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
};
